import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { usePublicSettings } from '@/hooks';
import { useMemo } from 'react';

/**
 * SEO Component
 * Reusable component for managing page-specific meta tags
 * Handles title, description, Open Graph, and Twitter cards
 * Uses dynamic settings from backend for social links and contact info
 */

interface SEOProps {
  /** Page title - will be appended with site name */
  title?: string;
  /** Meta description for search engines */
  description?: string;
  /** Canonical URL for the page */
  url?: string;
  /** Open Graph image URL (null/empty falls back to site default) */
  image?: string | null;
  /** Type of content (website, article, etc.) */
  type?: 'website' | 'article' | 'profile';
  /** Article published date (ISO 8601) */
  publishedTime?: string;
  /** Article modified date (ISO 8601) */
  modifiedTime?: string;
  /** Article author name */
  author?: string;
  /** Keywords for search engines */
  keywords?: string[];
  /** Whether to index this page */
  noIndex?: boolean;
  /** Additional structured data (JSON-LD) */
  structuredData?: object;
}

// Site-wide constants (using environment variables)
const SITE_NAME = 'YIM Burundi';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.yimburundi.com';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.webp`;
const TWITTER_HANDLE = '@YIM_BURUNDII';
const THEME_COLOR = '#1E40AF';

export function SEO({
  title,
  description,
  url,
  image,
  type = 'website',
  publishedTime,
  modifiedTime,
  author,
  keywords = [],
  noIndex = false,
  structuredData,
}: SEOProps) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'en';

  // Fetch settings for dynamic social links and contact info
  const { data: settings } = usePublicSettings();

  // Build full title
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;

  // Build full URL
  const fullUrl = url ? `${SITE_URL}${url}` : SITE_URL;

  // Default description
  const defaultDescription = currentLang === 'fr'
    ? "Youth Impact Mission Burundi - Autonomiser les jeunes leaders pour transformer leurs communautés par la foi, le service et le leadership."
    : 'Youth Impact Mission Burundi - Empowering young leaders to transform their communities through faith, service and leadership.';

  const metaDescription = description || defaultDescription;

  // null/undefined/empty from API must not crash OG image resolution
  const resolvedImage = image?.trim() || DEFAULT_IMAGE;
  const ogImage = resolvedImage.startsWith('http')
    ? resolvedImage
    : `${SITE_URL}${resolvedImage.startsWith('/') ? resolvedImage : `/${resolvedImage}`}`;

  // Default keywords
  const defaultKeywords = [
    'YIM Burundi',
    'Youth Impact Mission',
    'youth',
    'leadership',
    'Burundi',
    'mission',
    'training',
    'community',
    'faith',
    'personal development',
    'Africa',
  ];

  const allKeywords = [...new Set([...keywords, ...defaultKeywords])];

  // Build social links array from settings (filter out empty values)
  const socialLinks = useMemo(() => {
    const links: string[] = [];
    if (settings?.FACEBOOK_URL) links.push(settings.FACEBOOK_URL);
    if (settings?.INSTAGRAM_URL) links.push(settings.INSTAGRAM_URL);
    if (settings?.X_URL) links.push(settings.X_URL);
    if (settings?.LINKEDIN_URL) links.push(settings.LINKEDIN_URL);
    if (settings?.YOUTUBE_URL) links.push(settings.YOUTUBE_URL);
    return links;
  }, [settings]);

  // Organization structured data with dynamic settings
  const organizationData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Youth Impact Mission Burundi',
    alternateName: 'YIM Burundi',
    url: SITE_URL,
    logo: `${SITE_URL}/logo.webp`,
    sameAs: socialLinks.length > 0 ? socialLinks : [
      // Fallback values if settings not loaded
      'https://www.facebook.com/Yim.Burundi2014',
      'https://instagram.com/yim.burundi',
      'https://x.com/YIM_BURUNDII',
      'https://www.linkedin.com/in/youth-impact-mission-burundi-433184298/',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: settings?.PHONE || '+257-79-368-298',
      contactType: 'customer service',
      areaServed: 'BI',
      availableLanguage: ['French', 'English', 'Kirundi'],
    },
    ...(settings?.EMAIL && { email: settings.EMAIL }),
    ...(settings?.ADDRESS && {
      address: {
        '@type': 'PostalAddress',
        streetAddress: settings.ADDRESS,
        addressCountry: 'BI',
      }
    }),
  }), [settings, socialLinks]);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang={currentLang} />
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={allKeywords.join(', ')} />
      <meta name="author" content={author || SITE_NAME} />
      <meta name="theme-color" content={THEME_COLOR} />

      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />

      {/* Language alternates (SPA with no path-prefix routing) */}
      <link rel="alternate" hrefLang="fr" href={fullUrl} />
      <link rel="alternate" hrefLang="en" href={fullUrl} />
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={currentLang === 'fr' ? 'fr_FR' : 'en_US'} />

      {/* Article specific tags */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />

      {/* Structured Data - Organization */}
      <script type="application/ld+json">
        {JSON.stringify(organizationData)}
      </script>

      {/* Additional Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

/**
 * Pre-configured SEO components for common pages
 */

export function HomeSEO() {
  const { t } = useTranslation();

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'YIM Burundi',
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <SEO
      title={t('meta.home.title')}
      description={t('meta.home.description')}
      url="/"
      structuredData={structuredData}
    />
  );
}

export function AboutSEO() {
  const { t } = useTranslation();

  return (
    <SEO
      title={t('meta.about.title')}
      description={t('meta.about.description')}
      url="/about"
      keywords={['history', 'mission', 'vision', 'team', 'leadership']}
    />
  );
}

export function ActivitiesSEO() {
  const { t } = useTranslation();

  return (
    <SEO
      title={t('meta.activities.title')}
      description={t('meta.activities.description')}
      url="/activities"
      keywords={['activities', 'programs', 'training', 'mentoring', 'school clubs']}
    />
  );
}

export function LeadershipSEO() {
  const { t } = useTranslation();

  return (
    <SEO
      title={t('meta.leadership.title')}
      description={t('meta.leadership.description')}
      url="/yim-adn"
      keywords={['leadership', 'principles', 'YIM DNA', 'values', 'training']}
    />
  );
}

export function ContactSEO() {
  const { t } = useTranslation();

  return (
    <SEO
      title={t('meta.contact.title')}
      description={t('meta.contact.description')}
      url="/contact"
      keywords={['contact', 'email', 'phone', 'address', 'Bujumbura']}
    />
  );
}

export function DonateSEO() {
  const { t } = useTranslation();

  return (
    <SEO
      title={t('meta.donate.title')}
      description={t('meta.donate.description')}
      url="/donate"
      keywords={['donate', 'donation', 'support', 'contribution', 'impact']}
    />
  );
}

export function EventsSEO() {
  const { t } = useTranslation();

  return (
    <SEO
      title={t('meta.events.title')}
      description={t('meta.events.description')}
      url="/events"
      keywords={['events', 'retreat', 'conference', 'training', 'gathering']}
    />
  );
}

export function BlogSEO() {
  const { t } = useTranslation();

  return (
    <SEO
      title={t('meta.blog.title')}
      description={t('meta.blog.description')}
      url="/blog"
      keywords={['blog', 'articles', 'testimonials', 'news', 'stories']}
    />
  );
}

export function GetInvolvedSEO() {
  const { t } = useTranslation();

  return (
    <SEO
      title={t('meta.getInvolved.title')}
      description={t('meta.getInvolved.description')}
      url="/get-involved"
      keywords={['volunteer', 'agent', 'join', 'engagement', 'transformation']}
    />
  );
}

export function GallerySEO() {
  const { t } = useTranslation();

  return (
    <SEO
      title={t('meta.gallery.title')}
      description={t('meta.gallery.description')}
      url="/gallery"
      keywords={['gallery', 'photos', 'moments', 'events', 'memories']}
    />
  );
}

export function OurHistorySEO() {
  const { t } = useTranslation();

  return (
    <SEO
      title={t('meta.history.title')}
      description={t('meta.history.description')}
      url="/history"
      keywords={['history', 'YIM', 'foundation', 'journey', 'Burundi']}
    />
  );
}

/**
 * Admin SEO Component
 * Automatically sets noindex, nofollow for all admin pages
 * This ensures admin pages never appear in search engines
 */
export function AdminSEO({ title }: { title?: string }) {
  const fullTitle = title ? `${title} | Admin - YIM Burundi` : 'Admin - YIM Burundi';

  return (
    <SEO
      title={fullTitle}
      description="YIM Burundi Administration Dashboard"
      noIndex={true}
    />
  );
}

export default SEO;
